import { useCallback, useState } from 'react';
import { appQueryClient, queryKeys, useServerMutation, useServerQuery } from '@/shared/query';
import {
  fetchNotificationCounts,
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../services/notificationsApi';
import type {
  AppNotification,
  NotificationCounts,
  NotificationPage,
  NotificationTab,
} from '../services/notificationsApi';

/** How often the badge asks whether anything has arrived. */
const POLL_INTERVAL_MS = 30_000;

/** What the header draws before the first answer comes back: a bell with no badge. */
const NO_COUNTS: NotificationCounts = { all: 0, unread: 0, actionRequired: 0 };

interface UseNotificationsResult {
  items: AppNotification[];
  counts: NotificationCounts;
  tab: NotificationTab;
  isOpen: boolean;
  loading: boolean;
  /** True once a load has failed, so the panel can say so instead of looking empty. */
  failed: boolean;
  markingAll: boolean;
  selectTab: (tab: NotificationTab) => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  /** Marks one read and answers with its link, or null when it points nowhere. */
  openNotification: (item: AppNotification) => Promise<string | null>;
  markAllRead: () => void;
}

/**
 * The bell: how many are unread, what the open tab holds, and what a click does.
 *
 * <h2>Polled, not pushed</h2>
 *
 * There is no socket and no SSE in this platform, so the counts are re-read every half minute. That is the
 * right cadence for what they carry: a background import finishing, or an approval arriving, is worth
 * knowing about within a minute, and nothing here is time-critical enough to justify a connection per
 * signed-in user.
 *
 * <h2>Opening is not reading</h2>
 *
 * <p>This used to mark everything read the moment the panel opened. That was defensible when the panel was
 * one undifferentiated list, and is wrong now: the panel has an Unread tab, so clearing it on open would
 * empty the very tab the user came to look at, and it destroyed the one piece of state the user controls
 * without them asking. A notification is read when it is clicked, or when "Mark all as read" is pressed.
 *
 * <h2>Counts come from the server, never from the page</h2>
 *
 * <p>Each tab's label shows the count from `/counts`, not `items.length`: the list is one page of twenty
 * and the count is the whole tab, so counting what arrived would quietly cap every label at 20.
 */
export function useNotifications(enabled: boolean): UseNotificationsResult {
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<NotificationTab>('ALL');

  const countsQuery = useServerQuery<NotificationCounts>({
    queryKey: queryKeys.notifications.counts(),
    queryFn: fetchNotificationCounts,
    enabled,
    /*
      Polled even while the panel is shut -- that is the whole job of the badge. `refetchIntervalInBackground`
      is deliberately left off: a backgrounded tab polling every thirty seconds for a number nobody is
      looking at is traffic with no reader.
    */
    refetchInterval: POLL_INTERVAL_MS,
    /* Shorter than the global 60s, or the first poll after opening would serve the answer from the cache. */
    staleTime: POLL_INTERVAL_MS,
    /*
      And on coming back to the tab. The global default is off, deliberately -- a silent re-read under a form
      a user is filling in moves rows beneath them. A number in the header is the opposite case: it is stale
      precisely while they are away, and it is the first thing they look at when they return.
    */
    refetchOnWindowFocus: true,
  });

  const listQuery = useServerQuery<NotificationPage>({
    queryKey: queryKeys.notifications.list(tab),
    queryFn: () => fetchNotifications(tab),
    /* Nothing is fetched until the panel is open: a list nobody has asked to see is a request nobody needs. */
    enabled: enabled && isOpen,
  });

  /*
    The application's client by name, not `useQueryClient`: `useServerQuery` names it too, and that is what
    lets the bell render under any test harness without a provider wrapped around it. Reaching for the
    context here would have quietly undone that for this one component.
  */
  const invalidate = useCallback(() => {
    /* Both keys: a read changes the badge and the Unread tab, and neither is recomputable from the other. */
    void appQueryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
  }, []);

  const readOne = useServerMutation<AppNotification, Error, number>({
    mutationFn: markNotificationRead,
    onSuccess: invalidate,
  });

  const readAll = useServerMutation<number, Error, void>({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidate,
  });

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((previous) => !previous);
  }, []);

  const selectTab = useCallback((next: NotificationTab) => {
    setTab(next);
  }, []);

  /**
   * The click: mark it read, then say where to go.
   *
   * <p>The link comes from the mutation's own response rather than from the row the panel drew, so the
   * caller follows the destination the server has just confirmed. A notification that cannot be marked
   * read still navigates — the user asked to open a record, and failing to record that they read it is
   * not a reason to refuse them the record.
   */
  const openNotification = useCallback(
    async (item: AppNotification): Promise<string | null> => {
      try {
        const confirmed = await readOne.mutateAsync(item.id);
        return confirmed.link;
      } catch {
        return item.link;
      }
    },
    [readOne],
  );

  const markAllRead = useCallback(() => {
    readAll.mutate();
  }, [readAll]);

  return {
    items: listQuery.data?.content ?? [],
    counts: countsQuery.data ?? NO_COUNTS,
    tab,
    isOpen,
    loading: listQuery.isPending && listQuery.fetchStatus !== 'idle',
    failed: listQuery.isError,
    markingAll: readAll.isPending,
    selectTab,
    open,
    close,
    toggle,
    openNotification,
    markAllRead,
  };
}
